package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.Mind_map
import com.tedd.db.Mind_map_node
import com.tedd.db.Mind_map_node_note
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Cartes heuristiques : la carte, ses nœuds en arbre, les notes rattachées.
 *
 * Les rangs parmi les frères sont des entiers espacés de [GAP] : insérer entre deux
 * frères prend le milieu, et on ne renumérote toute la fratrie que quand il n'y a
 * plus de place. Moins de lignes modifiées, donc moins de trafic de synchronisation.
 */
class MindMapRepository(private val db: TeddDatabase) {

    /** Ce qu'une suppression a touché, pour pouvoir l'annuler. */
    class Deletion(val nodeIds: List<String>, val linkIds: List<String>, val noteIds: List<String>) {
        val isEmpty: Boolean get() = nodeIds.isEmpty()
    }

    fun observeAll(): Flow<List<Mind_map>> =
        db.mindMapQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeById(id: String): Flow<Mind_map?> =
        db.mindMapQueries.selectById(id).asFlow().mapToOneOrNull(Dispatchers.Default)

    fun observeBySkill(skillId: String): Flow<List<Mind_map>> =
        db.mindMapQueries.selectBySkill(skillId).asFlow().mapToList(Dispatchers.Default)

    fun observeByObjective(objectiveId: String): Flow<List<Mind_map>> =
        db.mindMapQueries.selectByObjective(objectiveId).asFlow().mapToList(Dispatchers.Default)

    fun observeNodes(mapId: String): Flow<List<Mind_map_node>> =
        db.mindMapNodeQueries.selectByMap(mapId).asFlow().mapToList(Dispatchers.Default)

    fun observeLinks(mapId: String): Flow<List<Mind_map_node_note>> =
        db.mindMapNodeNoteQueries.selectByMap(mapId).asFlow().mapToList(Dispatchers.Default)

    fun observeLinksOfNode(nodeId: String): Flow<List<Mind_map_node_note>> =
        db.mindMapNodeNoteQueries.selectByNode(nodeId).asFlow().mapToList(Dispatchers.Default)

    suspend fun get(id: String): Mind_map? = withContext(Dispatchers.Default) {
        db.mindMapQueries.selectById(id).executeAsOneOrNull()
    }

    suspend fun nodes(mapId: String): List<Mind_map_node> = withContext(Dispatchers.Default) {
        db.mindMapNodeQueries.selectByMap(mapId).executeAsList()
    }

    suspend fun firstBySkill(skillId: String): Mind_map? = withContext(Dispatchers.Default) {
        db.mindMapQueries.selectBySkill(skillId).executeAsList().firstOrNull()
    }

    /** Crée une carte et sa racine, d'un seul tenant. Retourne l'identifiant de la carte. */
    suspend fun create(
        title: String,
        skillId: String? = null,
        objectiveId: String? = null,
        color: String? = null,
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val mapId = newId("mm")
        db.transaction {
            db.mindMapQueries.insert(mapId, title.trim(), color, skillId, objectiveId, 0L, now, now)
            db.mindMapNodeQueries.insert(newId("mmn"), mapId, null, title.trim(), null, null, null, 0L, now, now)
        }
        mapId
    }

    /** Ajoute un enfant en dernier. Retourne l'identifiant du nœud créé. */
    suspend fun addChild(parentId: String, text: String = ""): String? = withContext(Dispatchers.Default) {
        val parent = db.mindMapNodeQueries.selectById(parentId).executeAsOneOrNull() ?: return@withContext null
        val now = nowMillis()
        val last = db.mindMapNodeQueries.selectChildren(parentId).executeAsList().lastOrNull()
        val id = newId("mmn")
        db.mindMapNodeQueries.insert(id, parent.map_id, parentId, text, null, null, null, (last?.order_index ?: 0L) + GAP, now, now)
        id
    }

    /** Ajoute un frère juste après [nodeId]. La racine n'a pas de frère : retourne null. */
    suspend fun addSibling(nodeId: String, text: String = ""): String? = withContext(Dispatchers.Default) {
        val node = db.mindMapNodeQueries.selectById(nodeId).executeAsOneOrNull() ?: return@withContext null
        val parentId = node.parent_id ?: return@withContext null
        val now = nowMillis()
        val id = newId("mmn")
        db.transaction {
            val order = orderAfter(parentId, nodeId, now)
            db.mindMapNodeQueries.insert(id, node.map_id, parentId, text, null, null, null, order, now, now)
        }
        id
    }

    /** Renomme un nœud ; la racine renomme aussi la carte. */
    suspend fun rename(nodeId: String, text: String) = withContext(Dispatchers.Default) {
        val node = db.mindMapNodeQueries.selectById(nodeId).executeAsOneOrNull() ?: return@withContext
        val clean = text.trim()
        if (clean == node.text) return@withContext
        val now = nowMillis()
        db.transaction {
            db.mindMapNodeQueries.setText(clean, now, nodeId)
            if (node.parent_id == null) db.mindMapQueries.setTitle(clean, now, node.map_id)
        }
    }

    suspend fun setNodeColor(nodeId: String, color: String?) = withContext(Dispatchers.Default) {
        db.mindMapNodeQueries.setColor(color, nowMillis(), nodeId)
    }

    suspend fun setColor(mapId: String, color: String?) = withContext(Dispatchers.Default) {
        db.mindMapQueries.setColor(color, nowMillis(), mapId)
    }

    suspend fun setAttachments(mapId: String, skillId: String?, objectiveId: String?) = withContext(Dispatchers.Default) {
        db.mindMapQueries.setAttachments(skillId, objectiveId, nowMillis(), mapId)
    }

    /** Monte (delta < 0) ou descend (delta > 0) un nœud parmi ses frères. */
    suspend fun moveAmongSiblings(nodeId: String, delta: Int): Boolean = withContext(Dispatchers.Default) {
        val node = db.mindMapNodeQueries.selectById(nodeId).executeAsOneOrNull() ?: return@withContext false
        val parentId = node.parent_id ?: return@withContext false
        val siblings = db.mindMapNodeQueries.selectChildren(parentId).executeAsList()
        val from = siblings.indexOfFirst { it.id == nodeId }
        val to = from + delta
        if (from < 0 || to < 0 || to >= siblings.size || delta == 0) return@withContext false
        val reordered = siblings.toMutableList().apply { add(to, removeAt(from)) }
        val now = nowMillis()
        db.transaction {
            reordered.forEachIndexed { i, n ->
                val wanted = (i + 1) * GAP
                if (n.order_index != wanted) db.mindMapNodeQueries.setOrder(wanted, now, n.id)
            }
        }
        true
    }

    /**
     * Déplace un nœud sous [newParentId], en dernier. Refuse de faire d'un nœud son
     * propre descendant, de déplacer la racine, ou de changer de carte.
     */
    suspend fun reparent(nodeId: String, newParentId: String): Boolean = withContext(Dispatchers.Default) {
        if (nodeId == newParentId) return@withContext false
        val node = db.mindMapNodeQueries.selectById(nodeId).executeAsOneOrNull() ?: return@withContext false
        if (node.parent_id == null) return@withContext false
        val parent = db.mindMapNodeQueries.selectById(newParentId).executeAsOneOrNull() ?: return@withContext false
        if (parent.map_id != node.map_id) return@withContext false
        val all = db.mindMapNodeQueries.selectByMap(node.map_id).executeAsList()
        if (isDescendant(all, candidate = newParentId, ancestor = nodeId)) return@withContext false
        val last = all.filter { it.parent_id == newParentId }.maxByOrNull { it.order_index }
        db.mindMapNodeQueries.move(newParentId, (last?.order_index ?: 0L) + GAP, nowMillis(), nodeId)
        true
    }

    /**
     * Supprime un nœud et tout ce qu'il porte : ses descendants, leurs liens vers des
     * notes, et les notes écrites dans ces nœuds. Les notes de la bibliothèque ne
     * perdent que le lien. La racine ne se supprime pas ainsi : voir [deleteMap].
     */
    suspend fun deleteNode(nodeId: String): Deletion = withContext(Dispatchers.Default) {
        val node = db.mindMapNodeQueries.selectById(nodeId).executeAsOneOrNull() ?: return@withContext Deletion(emptyList(), emptyList(), emptyList())
        if (node.parent_id == null) return@withContext Deletion(emptyList(), emptyList(), emptyList())
        val all = db.mindMapNodeQueries.selectByMap(node.map_id).executeAsList()
        val ids = subtree(all, nodeId)
        val now = nowMillis()
        val linkIds = mutableListOf<String>()
        val noteIds = mutableListOf<String>()
        db.transaction {
            for (id in ids) {
                db.mindMapNodeNoteQueries.selectByNode(id).executeAsList().forEach {
                    db.mindMapNodeNoteQueries.softDelete(now, now, it.id)
                    linkIds += it.id
                }
                db.noteQueries.selectByParent(ParentKind.MindMapNode.raw, id).executeAsList().forEach {
                    db.noteQueries.softDelete(now, now, it.id)
                    noteIds += it.id
                }
                db.mindMapNodeQueries.softDelete(now, now, id)
            }
        }
        Deletion(ids, linkIds, noteIds)
    }

    /** Annule une suppression toute récente. */
    suspend fun restore(deletion: Deletion) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            deletion.nodeIds.forEach { db.mindMapNodeQueries.restore(now, it) }
            deletion.linkIds.forEach { db.mindMapNodeNoteQueries.restore(now, it) }
            deletion.noteIds.forEach { db.noteQueries.restore(now, it) }
        }
    }

    /** Supprime la carte entière, nœuds, liens et notes écrites dans les nœuds compris. */
    suspend fun deleteMap(mapId: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            db.mindMapNodeQueries.selectByMap(mapId).executeAsList().forEach { node ->
                db.mindMapNodeNoteQueries.selectByNode(node.id).executeAsList().forEach { db.mindMapNodeNoteQueries.softDelete(now, now, it.id) }
                db.noteQueries.selectByParent(ParentKind.MindMapNode.raw, node.id).executeAsList().forEach { db.noteQueries.softDelete(now, now, it.id) }
                db.mindMapNodeQueries.softDelete(now, now, node.id)
            }
            db.mindMapQueries.softDelete(now, now, mapId)
        }
    }

    /**
     * Rattache une note de la bibliothèque. Sans effet si le lien existe déjà. Un lien
     * supprimé mais jamais envoyé au serveur est réveillé ; un lien déjà synchronisé
     * supprimé est remplacé par un neuf (la restauration ne voyage pas encore).
     */
    suspend fun attachNote(nodeId: String, noteId: String): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val existing = db.mindMapNodeNoteQueries.selectByNodeAnyState(nodeId).executeAsList().filter { it.note_id == noteId }
        existing.firstOrNull { it.is_deleted == 0L }?.let { return@withContext it.id }
        val revivable = existing.firstOrNull { it.synced_at == null }
        if (revivable != null) {
            db.mindMapNodeNoteQueries.restore(now, revivable.id)
            return@withContext revivable.id
        }
        val last = db.mindMapNodeNoteQueries.selectByNode(nodeId).executeAsList().lastOrNull()
        val id = newId("mml")
        db.mindMapNodeNoteQueries.insert(id, nodeId, noteId, (last?.order_index ?: 0L) + GAP, now, now)
        id
    }

    suspend fun detachNote(nodeId: String, noteId: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.mindMapNodeNoteQueries.selectByNode(nodeId).executeAsList()
            .filter { it.note_id == noteId }
            .forEach { db.mindMapNodeNoteQueries.softDelete(now, now, it.id) }
    }

    // ── Conversion d'une compétence ───────────────────────────────────────────

    /** Un nœud que la compétence produit : sa provenance, celle de son parent, son texte, sa note. */
    private class Spec(val kind: String, val id: String, val parent: Pair<String, String>?, val text: String, val note: String? = null) {
        val key: Pair<String, String> get() = kind to id
    }

    /** Ce que la conversion créerait, pour l'annoncer : « 12 étapes, 5 objectifs… ». */
    suspend fun previewFromSkill(skillId: String): Map<String, Int> = withContext(Dispatchers.Default) {
        skillSpecs(skillId).drop(1).filter { it.kind != GROUP }.groupingBy { it.kind }.eachCount()
    }

    /**
     * Convertit une compétence en carte : la compétence en racine, puis ses branches
     * Parcours, Objectifs, Projets, Recueil et Cartes mémoire. Chaque nœud garde sa
     * provenance, ce qui permet de l'ouvrir et de mettre la carte à jour plus tard.
     * Retourne l'identifiant de la carte, ou null si la compétence n'existe pas.
     */
    suspend fun fromSkill(skillId: String): String? = withContext(Dispatchers.Default) {
        val skill = db.skillQueries.selectById(skillId).executeAsOneOrNull() ?: return@withContext null
        val specs = skillSpecs(skillId)
        val now = nowMillis()
        val mapId = newId("mm")
        db.transaction {
            db.mindMapQueries.insert(mapId, skill.name, skill.color, skillId, null, 0L, now, now)
            materialize(mapId, specs, emptyMap(), emptySet(), now)
        }
        mapId
    }

    /**
     * Ajoute à une carte issue d'une compétence ce qui manque encore : les éléments
     * nouveaux de la compétence. Ne touche pas aux nœuds existants, renommés ou
     * déplacés. Retourne le nombre de nœuds ajoutés.
     */
    suspend fun refreshFromSkill(mapId: String): Int = withContext(Dispatchers.Default) {
        val map = db.mindMapQueries.selectById(mapId).executeAsOneOrNull() ?: return@withContext 0
        val skillId = map.skill_id ?: return@withContext 0
        val specs = skillSpecs(skillId)
        // Un nœud supprimé par l'utilisateur compte comme présent : il ne revient pas.
        val all = db.mindMapNodeQueries.selectByMapAnyState(mapId).executeAsList().filter { it.source_kind != null && it.source_id != null }
        val live = all.filter { it.is_deleted == 0L }.associate { (it.source_kind!! to it.source_id!!) to it.id }
        val known = all.map { it.source_kind!! to it.source_id!! }.toSet()
        val now = nowMillis()
        var added = 0
        db.transaction { added = materialize(mapId, specs, live, known, now) }
        added
    }

    /**
     * Insère les nœuds absents, parents d'abord. [existing] : les nœuds vivants par
     * provenance (pour accrocher les nouveaux) ; [known] : toutes les provenances déjà
     * vues, supprimées comprises. Un nœud dont le parent manque est laissé de côté.
     */
    private fun materialize(
        mapId: String,
        specs: List<Spec>,
        existing: Map<Pair<String, String>, String>,
        known: Set<Pair<String, String>> = existing.keys,
        now: Long,
    ): Int {
        val ids = existing.toMutableMap()
        val nextOrder = mutableMapOf<String?, Long>()
        fun orderUnder(parentId: String?): Long {
            val current = nextOrder[parentId] ?: (
                if (parentId == null) 0L
                else (db.mindMapNodeQueries.selectChildren(parentId).executeAsList().lastOrNull()?.order_index ?: 0L)
            )
            val next = current + GAP
            nextOrder[parentId] = next
            return next
        }
        var added = 0
        for (spec in specs) {
            if (spec.key in ids || spec.key in known) continue
            val parentId = spec.parent?.let { ids[it] ?: continue }
            val id = newId("mmn")
            db.mindMapNodeQueries.insert(id, mapId, parentId, spec.text, null, spec.kind, spec.id, orderUnder(parentId), now, now)
            spec.note?.takeIf { it.isNotBlank() }?.let { body ->
                db.noteQueries.insert(newId("n"), ParentKind.MindMapNode.raw, id, null, body, null, 0L, 0L, null, null, 0L, now, now)
            }
            ids[spec.key] = id
            added++
        }
        return added
    }

    /** L'arbre qu'une compétence décrit, dans l'ordre d'insertion (parents avant enfants). */
    private fun skillSpecs(skillId: String): List<Spec> {
        val skill = db.skillQueries.selectById(skillId).executeAsOneOrNull() ?: return emptyList()
        val root = Spec("skill", skillId, null, skill.name, skill.intention)
        val out = mutableListOf(root)
        fun group(id: String, text: String): Pair<String, String> {
            out += Spec(GROUP, "$skillId/$id", root.key, text)
            return GROUP to "$skillId/$id"
        }
        // Jusqu'au dernier élément : les tâches (les « micro » du parcours) sous leur jalon,
        // leur objectif ou leur projet. Une tâche faite garde sa coche dans le texte.
        fun tasksUnder(parentKind: String, parentId: String, parent: Pair<String, String>) {
            db.taskQueries.selectByParent(parentKind, parentId).executeAsList().forEach { t ->
                val text = (if (t.status == "done") "✓ " else "") + t.title
                val note = listOfNotNull(t.description?.takeIf { it.isNotBlank() }, t.deliverable?.takeIf { it.isNotBlank() }?.let { "Livrable : $it" }, t.acceptance?.takeIf { it.isNotBlank() }?.let { "Critère : $it" })
                    .joinToString("\n\n")
                out += Spec("task", t.id, parent, text, note.ifBlank { null })
            }
        }
        fun objectiveWithMilestones(objectiveId: String, title: String, parent: Pair<String, String>, note: String?) {
            val key = "objective" to objectiveId
            out += Spec("objective", objectiveId, parent, title, note)
            db.milestoneQueries.selectByObjective(objectiveId).executeAsList().forEach { m ->
                val mKey = "milestone" to m.id
                out += Spec("milestone", m.id, key, m.title, m.description)
                tasksUnder("milestone", m.id, mKey)
            }
            tasksUnder("objective", objectiveId, key)
        }

        val steps = db.parcoursStepQueries.selectBySkill(skillId).executeAsList()
        val stepProjects = steps.mapNotNull { it.project_id }.toSet()
        if (steps.isNotEmpty()) {
            val parcours = group("parcours", "Parcours")
            for (step in steps) {
                val key = "parcours_step" to step.id
                out += Spec("parcours_step", step.id, parcours, listOf(step.code, step.title).filter { it.isNotBlank() }.joinToString(" · "), step.summary)
                if (step.kind == "projet" && !step.level_name.isNullOrBlank()) {
                    out += Spec("parcours_level", step.id, key, "Palier : ${step.level_name}", step.level_desc)
                }
                db.objectiveQueries.selectByStep(step.id).executeAsList().forEach { o ->
                    objectiveWithMilestones(o.id, o.title, key, o.description)
                }
            }
        }

        val stepIds = steps.map { it.id }.toSet()
        val freeObjectives = db.objectiveQueries.selectAll().executeAsList()
            .filter { it.skill_id == skillId && (it.step_id == null || it.step_id !in stepIds) }
        if (freeObjectives.isNotEmpty()) {
            val objectives = group("objectifs", "Objectifs")
            freeObjectives.forEach { o -> objectiveWithMilestones(o.id, o.title, objectives, o.description) }
        }

        val projects = db.projectQueries.selectAll().executeAsList().filter { it.skill_id == skillId && it.id !in stepProjects }
        if (projects.isNotEmpty()) {
            val group = group("projets", "Projets")
            projects.forEach { p ->
                out += Spec("project", p.id, group, p.name, p.description)
                tasksUnder("project", p.id, "project" to p.id)
            }
        }

        val competences = db.competenceQueries.selectBySkill(skillId).executeAsList()
        if (competences.isNotEmpty()) {
            val recueil = group("recueil", "Recueil")
            for (c in competences) {
                val key = "competence" to c.id
                out += Spec("competence", c.id, recueil, c.name, c.description)
                db.competenceCriterionQueries.selectByCompetence(c.id).executeAsList().forEach { cr ->
                    val level = if (cr.level > 0) " · niveau ${cr.level}" else ""
                    out += Spec("competence_criterion", cr.id, key, cr.label + level, cr.description)
                }
            }
        }

        val cards = db.flashcardQueries.selectAll().executeAsList().filter { it.skill_id == skillId }
        if (cards.isNotEmpty()) {
            val memo = group("cartes", "Cartes mémoire")
            val byBox = cards.size > MAX_FLAT_CARDS
            for (card in cards) {
                val parent = if (byBox) {
                    val boxKey = GROUP to "$skillId/boite-${card.box}"
                    if (out.none { it.key == boxKey }) out += Spec(GROUP, "$skillId/boite-${card.box}", memo, "Boîte ${card.box}")
                    boxKey
                } else memo
                val note = listOfNotNull(card.back.takeIf { it.isNotBlank() }, card.note?.takeIf { it.isNotBlank() }).joinToString("\n\n")
                out += Spec("flashcard", card.id, parent, card.front, note)
            }
        }
        return out
    }

    // ── Outils ────────────────────────────────────────────────────────────────

    /** Le rang à donner à un nouveau frère placé juste après [afterId] ; renumérote s'il faut. */
    private fun orderAfter(parentId: String, afterId: String, now: Long): Long {
        val siblings = db.mindMapNodeQueries.selectChildren(parentId).executeAsList()
        val index = siblings.indexOfFirst { it.id == afterId }
        val before = siblings[index].order_index
        val after = siblings.getOrNull(index + 1)?.order_index ?: return before + GAP
        if (after - before >= 2) return before + (after - before) / 2
        siblings.forEachIndexed { i, n -> db.mindMapNodeQueries.setOrder((i + 1) * GAP, now, n.id) }
        return (index + 1) * GAP + GAP / 2
    }

    private fun subtree(all: List<Mind_map_node>, rootId: String): List<String> {
        val childrenOf = all.groupBy { it.parent_id }
        val out = mutableListOf<String>()
        val stack = ArrayDeque<String>()
        stack.addLast(rootId)
        while (stack.isNotEmpty()) {
            val current = stack.removeLast()
            out += current
            childrenOf[current]?.forEach { stack.addLast(it.id) }
        }
        return out
    }

    /** True si [candidate] est [ancestor] lui-même ou l'un de ses descendants. */
    private fun isDescendant(all: List<Mind_map_node>, candidate: String, ancestor: String): Boolean =
        candidate in subtree(all, ancestor)

    companion object {
        /** Écart entre deux rangs frères, pour insérer sans tout renuméroter. */
        const val GAP = 1024L
        /** Provenance des nœuds de regroupement (« Parcours », « Objectifs »…). */
        const val GROUP = "group"
        /** Au-delà, les cartes mémoire sont rangées par boîte. */
        private const val MAX_FLAT_CARDS = 60
    }
}
